import { Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Reveal } from "@project/user/components/reveal";
import { Button } from "@project/components/ui/button";
import { HERO_IMAGES } from "@project/lib/stock-images";
import inKindImage from "@project/assets/in-kind.png";
import scrapImage from "@project/assets/scrap.png";
import {
  ArrowRight,
  Heart,
  Package,
  Truck,
  Users,
  ShieldCheck,
  HandHeart,
  Recycle,
} from "lucide-react";

export default function DonationTypeSelect() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Item Donation"
        title="Choose how you'd like to donate"
        subtitle="Give essentials or donate usable scrap items — both help us care better for our elders every day."
        image={HERO_IMAGES.donate}
      />
      <section className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
        <Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            <DonationTypeCard
              theme="green"
              icon={Package}
              title="In-Kind Donation"
              subtitle="Give essentials. Spread happiness."
              description="Donate items like food grains, new equipments, clothes, furniture and more. Your kindness helps us care better, every single day."
              features={[
                { icon: Users, label: "Support daily needs" },
                { icon: ShieldCheck, label: "100% goes to seva" },
                { icon: HandHeart, label: "Make a direct impact" },
              ]}
              image={inKindImage}
              imageAlt="In-kind donation box with grains and essentials"
              ctaLabel="Donate In-Kind Items"
              ctaIcon={Heart}
              to="/donate/inkind"
            />

            <DonationTypeCard
              theme="orange"
              icon={Truck}
              title="Scrap Donation"
              subtitle="Old for you, useful for us."
              description="Donate your old furniture, fridge, appliances and other usable items. We'll pick it up from your location twice a week in Ahmedabad."
              features={[
                { icon: Truck, label: "Free pickup service" },
                { icon: Recycle, label: "Reuse & reduce waste" },
                { icon: HandHeart, label: "Support a noble cause" },
              ]}
              image={scrapImage}
              imageAlt="Furniture and appliances for scrap donation"
              ctaLabel="Donate Scrap Items"
              ctaIcon={Truck}
              to="/donate/scrap"
            />
          </div>

          <p className="mt-8 flex items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <Heart className="text-leaf h-4 w-4" />
            Every donation, big or small, brings comfort, care and smiles to our
            elders.
          </p>
        </Reveal>
      </section>
    </PageShell>
  );
}

function DonationTypeCard({
  theme,
  icon: Icon,
  title,
  subtitle,
  description,
  features,
  image,
  imageAlt,
  ctaLabel,
  ctaIcon: CtaIcon,
  to,
}: {
  theme: "green" | "orange";
  icon: typeof Package;
  title: string;
  subtitle: string;
  description: string;
  features: { icon: typeof Users; label: string }[];
  image: string;
  imageAlt: string;
  ctaLabel: string;
  ctaIcon: typeof Heart;
  to: string;
}) {
  const isGreen = theme === "green";
  const cardBg = isGreen ? "bg-[#f0f9f4]" : "bg-[#fff7f0]";
  const borderColor = isGreen ? "border-leaf/20" : "border-gold/25";
  const iconBg = isGreen ? "bg-leaf/12 text-leaf" : "bg-gold/15 text-gold";
  const titleColor = isGreen ? "text-leaf" : "text-primary";
  const subtitleColor = isGreen ? "text-leaf/80" : "text-gold";
  const decorCircle1 = isGreen ? "bg-leaf/10" : "bg-gold/12";
  const decorCircle2 = isGreen ? "bg-leaf/8" : "bg-gold/10";
  const btnClass = isGreen
    ? "bg-leaf hover:bg-leaf/90 text-primary-foreground"
    : "hover:bg-gold/90 bg-gold text-gold-foreground";

  return (
    <div
      className={`relative min-h-[365px] overflow-hidden rounded-3xl border shadow-md ${cardBg} ${borderColor}`}
    >
      <div
        className={`pointer-events-none absolute -right-4 top-10 h-36 w-36 rounded-full ${decorCircle1}`}
      />
      <div
        className={`pointer-events-none absolute bottom-20 right-0 h-44 w-44 rounded-full ${decorCircle2}`}
      />

      <div className="pointer-events-none absolute bottom-[-58px] left-[150px] z-0 h-auto w-[80%] opacity-45 [isolation:isolate] md:bottom-[-62px] md:left-[210px]">
        <img
          src={image}
          alt={imageAlt}
          className="h-full w-full object-contain [object-position:right_bottom]"
          style={{ mixBlendMode: "screen" }}
        />
      </div>

      <div className="relative z-10 flex min-h-[365px] flex-col p-6 md:p-7">
        <div className="flex items-start gap-3">
          <div
            className={`grid h-12 w-12 shrink-0 place-items-center rounded-full ${iconBg}`}
          >
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <h2
              className={`font-serif text-xl font-bold md:text-2xl ${titleColor}`}
            >
              {title}
            </h2>
            <p className={`mt-0.5 text-sm font-semibold ${subtitleColor}`}>
              {subtitle}
            </p>
          </div>
        </div>

        <p className="mt-4 w-full text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>

        <div className="mt-auto space-y-5 pt-6">
          <div className="flex flex-col gap-2.5">
            {features.map(f => (
              <div
                key={f.label}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground"
              >
                <f.icon
                  className={`h-4 w-4 shrink-0 ${isGreen ? "text-leaf" : "text-gold"}`}
                />
                {f.label}
              </div>
            ))}
          </div>

          <Button
            asChild
            size="lg"
            className={`max-w-full gap-2 sm:max-w-[58%] ${btnClass}`}
          >
            <Link to={to}>
              <CtaIcon className="h-4 w-4" />
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
